// ============================================================================
// JARVISTRAVEL - A NEW BUILD WHILE THE APP IS OPEN (JAR-2323)
// The service worker registration, and the check that finds a deploy made
// while the app was open.
//
// The browser looks for a new service worker only when a page loads, and this
// app rarely loads a page, so a tab left open ran the old build for over an
// hour after a deploy, against a server whose contract had moved on. So the app
// looks whenever it comes back into view, and on an interval while it's open.
//
// The build is autoUpdate, so a new worker takes over as soon as it's found.
// The page still runs the old code until it reloads. That's offered with one
// tap (UpdateReadyBanner) rather than done under someone who's typing.
//
// Each screen's code loads on its first visit, and after a deploy the old
// build's files are gone, from the worker's cache and from the server. So the
// first visit to a screen then fails, and that one reloads at once (JAR-2490).
// It's the one reload that doesn't wait for a tap: the screen can't show without
// it, since React.lazy keeps the failed load, and anything unsaved on the screen
// being left goes with the old page. It happens once per tab, and offline only
// when the service worker can serve the new build.
// ============================================================================

import { registerSW } from 'virtual:pwa-register';

/** How often an open app looks for a new build. */
export const APP_UPDATE_CHECK_MS = 30 * 60_000;

/** Set in a tab once it has reloaded for a missing file. A build that really
 *  lacks a file then shows its error instead of reloading on every visit. */
const STALE_FILE_RELOADED_KEY = 'jt.staleFileReloaded';

let ready = false;
const listeners = new Set<() => void>();
let stopChecking: (() => void) | null = null;
let stopReloading: (() => void) | null = null;
let reloadingForStaleFile = false;

/** Subscribe to "a new build is ready". Returns an unsubscribe. */
export function subscribeAppUpdate(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export const isAppUpdateReady = (): boolean => ready;

/** True once this page has started reloading for a missing file, so the screen
 *  that failed shows and reports nothing in the moment before it goes. */
export const isReloadingForStaleFile = (): boolean => reloadingForStaleFile;

/** Registers the service worker, starts looking for new builds, and reloads a
 *  page that asks for a file a deploy removed. */
export function initAppUpdates(reload: () => void = () => window.location.reload()): void {
  // Vite reports a screen's code that failed to load as vite:preloadError.
  // Its message is the same for a file a deploy removed and a network that
  // failed. Offline, the reload reaches the new build only through the service
  // worker; with none controlling the page it would land on the browser's
  // offline page, so the error shows instead.
  const onStaleFile = () => {
    if (!navigator.onLine && !navigator.serviceWorker?.controller) return;
    try {
      if (sessionStorage.getItem(STALE_FILE_RELOADED_KEY)) return;
      sessionStorage.setItem(STALE_FILE_RELOADED_KEY, '1');
    } catch {
      return; // with no storage nothing guards against a loop, so the error shows
    }
    reloadingForStaleFile = true;
    reload();
  };
  window.addEventListener('vite:preloadError', onStaleFile);
  stopReloading = () => window.removeEventListener('vite:preloadError', onStaleFile);

  registerSW({
    immediate: true,
    onNeedReload() {
      ready = true;
      listeners.forEach((fn) => fn());
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // A failed check, as when offline, just waits for the next one.
      const check = () => void registration.update().catch(() => {});
      const onVisible = () => {
        if (document.visibilityState === 'visible') check();
      };
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', check);
      const interval = setInterval(check, APP_UPDATE_CHECK_MS);
      stopChecking = () => {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', check);
        clearInterval(interval);
      };
    },
  });
}

export function reloadToUpdate(): void {
  window.location.reload();
}

/** Test seam: forget any registration, readiness and listeners. */
export function __resetAppUpdate(): void {
  stopChecking?.();
  stopChecking = null;
  stopReloading?.();
  stopReloading = null;
  reloadingForStaleFile = false;
  ready = false;
  listeners.clear();
}
